import React, { useState } from "react"
import { ContentfulConditionalGate } from "../../lib/contentful/components/ContentfulConditionGate"
import { Paths } from "../../utils/Paths"
import { PageNav } from "./DBGNavLinks"
import { FormattedMessage, useIntl } from "react-intl"
import {
  Box,
  Button,
  CloseIcon,
  Drawer,
  IconButton,
  NavigationIcon,
  Tile,
  useMediaQuery,
  useTheme
} from "@carrotfertility/carotene-core"
import { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"
import { TypePageSkeleton } from "#/types/contentful"

type DigitalBenefitsGuideNavFields = {
  pages: EntryWithoutUnresolvableLinks<TypePageSkeleton>[]
}

const DigitalBenefitsGuideNavLinks = ({ pages }: DigitalBenefitsGuideNavFields): JSX.Element => {
  return (
    <>
      {pages?.map(
        (page) =>
          page.fields && (
            <ContentfulConditionalGate conditions={page.fields.conditions} key={page.sys.id}>
              <PageNav page={page} baseSlug={`${Paths.YOUR_BENEFIT_GUIDE}`} top={pages[0] === page} />
            </ContentfulConditionalGate>
          )
      )}
    </>
  )
}

const DigitalBenefitsGuideDesktopNav = ({ pages }: DigitalBenefitsGuideNavFields): JSX.Element => {
  return (
    <Tile
      component="nav"
      variant="secondary"
      color="blue"
      sx={{
        minInlineSize: "13.75rem",
        inlineSize: "13.75rem"
      }}
    >
      <DigitalBenefitsGuideNavLinks pages={pages} />
    </Tile>
  )
}

const MobileNavButton = ({
  displayNavDrawer,
  setDisplayNavDrawer
}: {
  displayNavDrawer: boolean
  setDisplayNavDrawer: (inputValue: boolean) => void
}): JSX.Element => {
  return (
    <Button
      variant="outlined"
      onClick={() => setDisplayNavDrawer(!displayNavDrawer)}
      startIcon={<NavigationIcon />}
      color="secondary"
    >
      <FormattedMessage defaultMessage="Benefit guide menu" />
    </Button>
  )
}

const DrawerCloseButton = ({ onClick }: { onClick: () => void }): JSX.Element => {
  const intl = useIntl()
  const ariaLabel = intl.formatMessage({
    defaultMessage: "Close drawer",
    description:
      "Drawer refers to a website navigation drawer (sidebar menu) on this page that opens when 'Benefit guide menu' is clicked. The drawer only appears on smaller screen sizes (mobile and some tablets)."
  })
  return (
    <Box
      sx={{ display: "flex", justifyContent: "flex-end", padding: (theme) => theme.spacing(theme.tokens.spacing.md) }}
    >
      <IconButton aria-label={ariaLabel} onClick={onClick}>
        <CloseIcon />
      </IconButton>
    </Box>
  )
}

const DigitalBenefitsGuideMobileNavDrawer = ({ pages }: DigitalBenefitsGuideNavFields): JSX.Element => {
  const [displayNavDrawer, setDisplayNavDrawer] = useState(false)

  return (
    <>
      <Box
        sx={(theme) => ({
          display: "flex",
          justifyContent: "flex-end",
          flexDirection: "row",
          alignItems: "center",
          gap: "small",
          marginBlockEnd: theme.tokens.spacing.lg,
          [theme.breakpoints.down("md")]: {
            marginBlockStart: "-8px"
          },
          [theme.breakpoints.only("lg")]: {
            marginBlockStart: "-16px"
          },
          [theme.breakpoints.up("lg")]: {
            marginBlockStart: "0"
          }
        })}
      >
        <MobileNavButton setDisplayNavDrawer={setDisplayNavDrawer} displayNavDrawer={displayNavDrawer} />
      </Box>
      <Drawer
        sx={(theme) => ({
          "& .MuiDrawer-paper": {
            [theme.breakpoints.down("sm")]: {
              inlineSize: "256px"
            },
            [theme.breakpoints.up("sm")]: {
              inlineSize: "320px"
            },
            [theme.breakpoints.up("lg")]: {
              inlineSize: 0
            }
          }
        })}
        anchor="right"
        open={displayNavDrawer}
        onClose={() => setDisplayNavDrawer(false)}
      >
        <DrawerCloseButton onClick={() => setDisplayNavDrawer(false)} />
        <Box
          sx={(theme) => ({
            paddingInline: theme.tokens.spacing.lg,
            paddingBlock: theme.tokens.spacing.sm
          })}
        >
          <DigitalBenefitsGuideNavLinks pages={pages} />
        </Box>
      </Drawer>
    </>
  )
}

export const DigitalBenefitsGuideNav = ({ pages }: DigitalBenefitsGuideNavFields): JSX.Element => {
  const theme = useTheme()
  const showMobileNav = useMediaQuery(theme.breakpoints.down("lg"))

  if (showMobileNav) {
    return <DigitalBenefitsGuideMobileNavDrawer pages={pages} />
  }

  return <DigitalBenefitsGuideDesktopNav pages={pages} />
}
